import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { merge, Observable, take } from 'rxjs';

interface TrackedModal {
  /** How many history entries above its page this modal's entry sits (1 for the first modal). */
  depth: number;
  close(): void;
  /** Closed by back/forward or a page change, so its history entry is already handled. */
  closedExternally: boolean;
}

/**
 * Gives every open modal (Material dialogs, including the PDF preview, and ng-bootstrap modals)
 * its own browser history entry on the same URL, so the browser's back button closes the top
 * modal instead of leaving the page underneath it.
 *
 * Closing a modal any other way (X, Esc, backdrop, a Save button) steps back over its entry,
 * so history never collects dead steps.
 */
@Injectable({ providedIn: 'root' })
export class ModalHistoryService {
  /** history.state key holding the entry's modal depth. Page entries don't have it (depth 0). */
  static readonly STATE_KEY = 'rnavModalDepth';

  private readonly stack: TrackedModal[] = [];
  private readonly trackedNgbModals = new WeakSet<NgbModalRef>();
  /** Back steps this service has started and not yet seen the popstate for. */
  private pendingBacks = 0;
  /** The modal depth of the entry history will be on once pending back steps land. */
  private expectedDepth = 0;
  /** Modals opened while a back step was pending; their entries are pushed once it lands. */
  private readonly queuedEntries: TrackedModal[] = [];
  /** URL of the page currently shown; modal entries share it. */
  private pageUrl: string | undefined;
  private initialized = false;

  constructor(private location: Location, private router: Router, private dialog: MatDialog,
    private ngbModal: NgbModal) { }

  init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.pageUrl = this.location.path(true);

    this.dialog.afterOpened.subscribe(ref => this.track(() => ref.close(), ref.beforeClosed()));
    this.ngbModal.activeInstances.subscribe(refs => refs
      .filter(ref => !this.trackedNgbModals.has(ref))
      .forEach(ref => {
        this.trackedNgbModals.add(ref);
        this.track(() => ref.dismiss('back'), merge(ref.closed, ref.dismissed));
      }));

    this.location.subscribe(event => {
      if (event.type === 'popstate') {
        this.onPopState(event.url, event.state);
      }
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        // The page underneath is going away, so its modals go with it.
        this.closeModalsAbove(0);
      } else if (event instanceof NavigationEnd) {
        this.pageUrl = this.location.path(true);
        // Back/forward can land on a modal entry whose modal is long gone; step over it.
        this.dropDeadEntries();
      }
    });
  }

  private track(close: () => void, closing: Observable<unknown>): void {
    const top = this.stack[this.stack.length - 1];
    const depth = Math.max(top?.depth ?? 0, this.currentDepth()) + 1;
    const modal: TrackedModal = { depth, close, closedExternally: false };
    this.stack.push(modal);
    if (this.pendingBacks) {
      this.queuedEntries.push(modal);
    } else {
      this.pushEntry(modal);
    }
    closing.pipe(take(1)).subscribe(() => this.onModalClosed(modal));
  }

  private onModalClosed(modal: TrackedModal): void {
    this.remove(modal);
    if (modal.closedExternally) {
      return;
    }
    // Wait for synchronous follow-up code: a modal action that closes and then navigates
    // should leave via that navigation, not be undone by a back step.
    setTimeout(() => {
      if (!this.router.currentNavigation()) {
        this.dropDeadEntries();
      }
    });
  }

  private onPopState(url: string | undefined, state: unknown): void {
    if (this.pendingBacks) {
      // One of our own back steps landed.
      this.pendingBacks--;
      if (!this.pendingBacks) {
        this.queuedEntries.splice(0).forEach(modal => this.pushEntry(modal));
      }
      return;
    }
    if (url !== this.pageUrl) {
      // A different page: the router navigates, which closes the modals (NavigationStart)
      // and steps over dead entries (NavigationEnd).
      return;
    }
    this.closeModalsAbove(ModalHistoryService.depthOf(state));
    this.dropDeadEntries();
  }

  /** Closes, top first, every modal whose entry is above `depth`. */
  private closeModalsAbove(depth: number): void {
    for (let i = this.stack.length - 1; i >= 0 && this.stack[i].depth > depth; i--) {
      const modal = this.stack[i];
      modal.closedExternally = true;
      this.remove(modal);
      modal.close();
    }
  }

  /** Steps back over modal entries that no longer have an open modal. */
  private dropDeadEntries(): void {
    const current = this.currentDepth();
    const wanted = this.stack.length ? this.stack[this.stack.length - 1].depth : 0;
    if (current > wanted) {
      this.pendingBacks++;
      this.expectedDepth = wanted;
      this.location.historyGo(wanted - current);
    }
  }

  private pushEntry(modal: TrackedModal): void {
    const state = { ...(this.location.getState() as object | null), [ModalHistoryService.STATE_KEY]: modal.depth };
    this.location.go(this.location.path(true), '', state);
  }

  private currentDepth(): number {
    return this.pendingBacks ? this.expectedDepth : ModalHistoryService.depthOf(this.location.getState());
  }

  private remove(modal: TrackedModal): void {
    const index = this.stack.indexOf(modal);
    if (index >= 0) {
      this.stack.splice(index, 1);
    }
  }

  private static depthOf(state: unknown): number {
    const depth = (state as Record<string, unknown> | null)?.[ModalHistoryService.STATE_KEY];
    return typeof depth === 'number' ? depth : 0;
  }
}
